import { IComponentOptions, IController, IOnChangesObjectOf } from "angular";
import { AssignPermissions } from "@gtmhub/configuration";
import { IAccountSettings } from "@gtmhub/core";
import { ISessionSettings } from "@gtmhub/sessions";
import { WarningModalService } from "@gtmhub/shared/components";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { toSessionSettings, toSessionUISettings } from "./methodology-section.utils";

type PermissionsEntity = "Metric" | "Goal";
export interface ISessionSettingsUI {
  assignGoalPermissions: AssignPermissions;
  assignMetricPermissions: AssignPermissions;
  multipleOwnersEnabledForGoals: boolean;
  multipleOwnersEnabledForMetrics: boolean;
  progressAggregationEnabled: boolean;
  metricsLimit: number;
  goalsLimit: number;
}

export interface IMethodologySectionBindings {
  sessionSettings: ISessionSettings;
  disabled: boolean;
  emitSettings({ settings: ISessionSettings }): void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface MethodologySectionCtrl extends IMethodologySectionBindings {}
export class MethodologySectionCtrl implements IController {
  private accountSettings: IAccountSettings;
  defaultSettings: ISessionSettings;
  settings: ISessionSettingsUI;
  isOpened = false;
  isLoaded = false;
  settingsAreCustomized = false;

  public static $inject = ["AccountResolverService", "WarningModalService"];

  constructor(private accountResolverService: AccountResolverService, private warningModalService: WarningModalService) {
    this.accountSettings = this.parseAccountSettingsStr();
    this.defaultSettings = this.generateDefaultSessionSettings();
  }

  $onChanges(onChangesObj: IOnChangesObjectOf<IMethodologySectionBindings>) {
    if (onChangesObj.sessionSettings.isFirstChange()) {
      if (onChangesObj.sessionSettings.currentValue) {
        this.settings = toSessionUISettings(this.sessionSettings);
        this.isLoaded = true;
        this.checkSettingsDifference();
        return;
      }

      this.settings = toSessionUISettings(this.defaultSettings);
      this.isLoaded = true;
    } else {
      this.checkSettingsDifference();
    }
  }

  getLocalizationKeyForAssignPermissions(entity: PermissionsEntity): "employees_only" | "teams_only" | "employees_and_teams" {
    const permissions = this.settings[`assign${entity}Permissions`];
    switch (permissions) {
      case "user":
        return "employees_only";
      case "team":
        return "teams_only";
      case "user and team":
        return "employees_and_teams";
    }
  }

  changeAssignPermissions(entity: PermissionsEntity, type: AssignPermissions): void {
    this.settings[`assign${entity}Permissions`] = type;
    this.emitSettings({ settings: toSessionSettings(this.settings) });
  }

  changeAggregationSetting(body: { value: boolean }): void {
    this.settings.progressAggregationEnabled = body.value;
    this.emitSettings({ settings: toSessionSettings(this.settings) });
  }

  emit(): void {
    this.emitSettings({ settings: toSessionSettings(this.settings) });
  }

  resetToDefault(): void {
    const methodologyPerSessionEnabled = this.accountSettings.methodologyPerSessionEnabled;

    if (!methodologyPerSessionEnabled) {
      this.resetFromWarningModal();
    } else {
      this.setToDefault();
    }
  }

  private resetFromWarningModal(): void {
    this.warningModalService
      .warningModal({
        modalImage: "/dist/img/cannot-continue.svg",
        modalTitle: "reset_session_settings_confirm_title",
        modalText: "reset_session_settings_confirm_desc",
        confirmText: "reset",
        confirmBtnType: "destructive",
        dismissText: "cancel",
      })
      .then(() => {
        this.setToDefault();
      })
      .catch(() => {
        // we do not want to do anything for now if the user clicks on cancel
      });
  }

  private setToDefault(): void {
    this.settings = toSessionUISettings(this.defaultSettings);
    this.emitSettings({ settings: null });
  }

  private generateDefaultSessionSettings(): ISessionSettings {
    return {
      assignGoalPermissions: this.accountSettings.assignObjectivePermissions || "user and team",
      assignMetricPermissions: this.accountSettings.assignKRPermissions || "user and team",
      multipleOwners: {
        disabledForGoals: this.accountSettings.sharedOkrs?.objectives?.disabled || false,
        disabledForMetrics: this.accountSettings.sharedOkrs?.krs?.disabled || false,
      },
      progressAggregationDisabled: this.accountSettings.aggregation !== undefined ? !this.accountSettings.aggregation : false,
      goalsPerOwnerLimit: this.accountSettings.limitObjectives,
      metricsPerGoalLimit: this.accountSettings.limitKRs,
    };
  }

  private parseAccountSettingsStr(): IAccountSettings {
    const account = this.accountResolverService.getAccountData();
    const accountSettingsStr: string = (account && account.settings) || "";

    return accountSettingsStr ? JSON.parse(accountSettingsStr) : {};
  }

  private checkSettingsDifference(): void {
    if (!this.sessionSettings || !this.defaultSettings) {
      this.settingsAreCustomized = !!this.sessionSettings;
      return;
    }
    const sessionSettings = { ...this.sessionSettings };
    const defaultSettings = { ...this.defaultSettings };
    sessionSettings.goalsPerOwnerLimit = sessionSettings.goalsPerOwnerLimit || null;
    sessionSettings.metricsPerGoalLimit = sessionSettings.metricsPerGoalLimit || null;
    defaultSettings.goalsPerOwnerLimit = defaultSettings.goalsPerOwnerLimit || null;
    defaultSettings.metricsPerGoalLimit = defaultSettings.metricsPerGoalLimit || null;
    this.settingsAreCustomized = !angular.equals(sessionSettings, defaultSettings);
  }
}

export const MethodologySectionComponent: IComponentOptions = {
  templateUrl: require("./methodology-section.component.jade"),
  controller: MethodologySectionCtrl,
  bindings: {
    sessionSettings: "<",
    emitSettings: "&",
    disabled: "<",
  },
};
